import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';

/**
 * ComplianceSummaryCard
 * ----------------------
 * Compact, click-through summary of the org's Staff Compliance health.
 * Designed to live on the Organization tab as a roll-up — the full
 * drill-down still lives under Staff Ops → Compliance.
 *
 * Behaviour:
 *   - Calls GET /api/staff/compliance/summary (admin only).
 *   - Shows three counters (verified / pending / expiring soon / expired)
 *     and a CTA that takes the operator to the full Compliance panel.
 *   - Silently no-ops if the endpoint is missing (e.g. older backend
 *     deploy) so the rest of the Organization tab is never blocked.
 */
export const ComplianceSummaryCard = ({ onOpenFullPanel }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // The api util may or may not have a typed helper; fall back to
        // a direct fetch using the same auth header convention used
        // elsewhere in the codebase.
        const fn = api.getComplianceSummary || api.getStaffComplianceSummary;
        const data = fn ? await fn() : null;
        if (!cancelled && data) setSummary(data);
      } catch (err) {
        if (!cancelled) setError(err.message || '');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // If the endpoint isn't wired or the user has no compliance data, we
  // don't want to render an empty/broken card on the Organization tab.
  if (loading) {
    return (
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="compliance-summary-card-loading">
        <h3 style={{ marginTop: 0 }}>🛡️ Staff Compliance</h3>
        <div style={{ color: 'hsl(var(--muted-foreground))' }}>Checking…</div>
      </div>
    );
  }

  if (error || !summary) {
    return null;
  }

  // The summary endpoint shape is { total, by_status: { verified, pending,
  // rejected }, expiring_soon, expired, ... }. We code defensively so
  // older shapes still display something sensible.
  const total = summary.total ?? summary.total_records ?? 0;
  const byStatus = summary.by_status || summary.status_counts || {};
  const verified = byStatus.verified ?? summary.verified ?? 0;
  const pending = byStatus.pending ?? summary.pending ?? 0;
  const expiring = summary.expiring_soon ?? summary.expiring ?? 0;
  const expired = summary.expired ?? 0;

  const stat = (label, value, tone) => (
    <div
      key={label}
      style={{
        flex: 1, minWidth: 110, padding: '0.75rem 1rem',
        borderRadius: 8, border: '1px solid hsl(var(--border))',
        background: tone === 'warn'
          ? 'hsl(40 95% 96%)'
          : tone === 'danger'
            ? 'hsl(0 70% 96%)'
            : tone === 'good'
              ? 'hsl(140 50% 96%)'
              : 'hsl(var(--muted))',
      }}
      data-testid={`compliance-summary-${label.toLowerCase().replace(' ', '-')}`}
    >
      <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{label}</div>
    </div>
  );

  return (
    <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="compliance-summary-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>
          🛡️ Staff Compliance
          <span style={{
            marginLeft: '0.5rem', fontSize: '0.75rem', fontWeight: 400,
            color: 'hsl(var(--muted-foreground))',
          }}>
            {total} record{total === 1 ? '' : 's'} on file
          </span>
        </h3>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onOpenFullPanel}
          data-testid="open-full-compliance-panel-btn"
          title="Jump to the full Compliance dashboard under Staff Ops"
        >
          Open full panel →
        </button>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
        {stat('Verified', verified, 'good')}
        {stat('Pending', pending, 'warn')}
        {stat('Expiring soon', expiring, 'warn')}
        {stat('Expired', expired, 'danger')}
      </div>
      {(expired > 0 || expiring > 0) && (
        <p
          style={{ marginTop: '0.75rem', marginBottom: 0, fontSize: '0.85rem' }}
          data-testid="compliance-summary-callout"
        >
          {expired > 0 && (
            <>⚠️ <strong>{expired}</strong> document{expired === 1 ? '' : 's'} expired. </>
          )}
          {expiring > 0 && (
            <>⏳ <strong>{expiring}</strong> expiring soon.</>
          )}
        </p>
      )}
    </div>
  );
};

export default ComplianceSummaryCard;
